import { HttpClient } from "@angular/common/http";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DestroyBase } from "src/app/components/common/ng-destroy-base";
import { ReportDesignerTemplatePersistanceService } from "../../../services/report-designer-template-persistance.service";
import { AppServiceHelperService } from "../../../../../services/app-service-helper.service";
import { EventBusService } from "../../../../../services/event-bus.service";
import { AppLocalizationService } from "../../../../../services/AppLocalizationService";
import { AppSettingsService } from "../../../../../services/app-settings.service";
import { take } from "rxjs";
import { ReportDesignerStorageService } from "../../../services/report-designer-storage.service";
import { AppStorageService } from "../../../../../services/app-storage.service";

/**
 * Report Designer Save Template dialog
 */
@Component({
  selector: 'save-template-dialog',
  templateUrl: './save-template-dialog.component.html',
  styleUrls: ['./save-template-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SaveTemplateDialogComponent extends DestroyBase implements OnInit {
  /**
  * The dialog title.
  */
  dialogTitle: string;

  /**
  * Flag indicating if the dialog should be displayed
  */
  isDialogOpened = false;

  /**
   * Loading indicator
   */
  public loading = false;

  /**
  * Templates
  */
  templateData: any = null;

  /**
  * Existing templates
  */
  existingTemplatesNames: string[] = [];

  /**
   * Template name
   */
  templateName: string;

  /**
   * Template name is invalid?
   */
  templateNameInvalid = false;

  /**
   * Force new mode?
   */
  forceNewMode = false;

  /**
   * Save as new template?
   */
  saveAs = false;

  /**
   * Selectable languages
   */
  languageChoices: any[] = [];

  /**
   * Selected languages
   */
  languageIds: number[] = [];

  /**
   * Language choice invalid?
   */
  languageInvalid = false;

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.dialogTitle = this.translate.instant('titleTemplateSave');
    this.eventBus.subscribeEvent('saveTemplate', (params, _options) => {
      //prevent multiple calls to open dialog
      if (!this.isDialogOpened) {
        this.isDialogOpened = true;
        this.handleOpenDialogEvent(params);
      }
    });
  }

  /**
   * Constructor
   * @param appSettings
   * @param appLocalization
   * @param eventBus
   * @param translate
   * @param http
   * @param reportDesignerTemplatePersistanceService
   * @param appServiceHelper
   * @param appStorage
   * @param rdStorage
   */
  constructor(private appSettings: AppSettingsService,
    private appLocalization: AppLocalizationService,
    private eventBus: EventBusService,
    private translate: TranslateService,
    private http: HttpClient,
    private reportDesignerTemplatePersistanceService: ReportDesignerTemplatePersistanceService,
    private appServiceHelper: AppServiceHelperService,
    private appStorage: AppStorageService,
    private rdStorage: ReportDesignerStorageService) {
    super();
  }

  /**
   * Confirm event for dialog
   * */
  public confirm(): Promise<void> {
    return this.handleSaveTemplate();
  }

  /**
   * Cancel event for dialog
   * */
  cancel() {
    this.reset();
    this.isDialogOpened = false;
  }

  /**
   * Save as change handler
   */
  saveAsChanged() {
    const currentTemplate = this.rdStorage.getTemplate();
    if (currentTemplate && !this.saveAs) {
      this.templateName = currentTemplate.Name; //Reset the template name!
    }

    if (this.saveAs) {
      //if save as new remove possible inactive languages
      const activeLanguagesIds = this.appSettings.allReportDesignerLocales.filter(l => l.isActive).map(l => l.id);
      this.languageIds = this.languageIds.filter(id => activeLanguagesIds.indexOf(id) !== -1);
      this.languageChoices = this.languageChoices.filter(l => activeLanguagesIds.indexOf(l.id) !== -1);
    }
  }

  /**
  * Reset dialog message and title
  */
  private reset(): void {
    this.templateData = null;
    this.existingTemplatesNames = [];
    this.templateName = null;
    this.templateNameInvalid = false;
    this.forceNewMode = false;
    this.saveAs = false;
    this.languageChoices = [];
    this.languageIds = [];
    this.languageInvalid = false;
  }

  /**
   * Loads the templates
   * @returns
   */
  private async loadTemplates(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const loadUrl = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'templates/noJson');
      this.http.get<any>(loadUrl).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
   * Saves the template
   * @param template
   * @returns
   */
  private async saveTemplate(template): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const saveUrl = this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'templates')
      this.http.post<any>(saveUrl, template).pipe(take(1)).subscribe({
        next: (data: any) => { resolve(data); },
        error: (error: any) => { reject(error); }
      });
    });
  }

  /**
  * Handler for open dialog event
  */
  private async handleOpenDialogEvent(params) {
    this.reset();

    try {
      this.loading = true;
      const allTemplates = await this.loadTemplates();
      this.existingTemplatesNames = allTemplates.filter(item => item.InvisibleFlag != 1).map(t => t.Name);
      this.templateName = '';

      const currentTemplate = this.rdStorage.getTemplate();
      if (!currentTemplate) {
        this.forceNewMode = true;
        this.saveAs = true;
      }
      else {
        this.saveAs = false;
        if (this.appStorage.userIsInRole(['Admin', 'Project Team'])) {
          this.forceNewMode = false;
        }
        else {
          const userDetails = this.appStorage.getCurrentUserDetails();
          if (userDetails.OID == currentTemplate.createdById) {
            this.forceNewMode = false;
          }
          else {
            this.forceNewMode = true;
            this.saveAs = true;
          }
        }

        this.templateName = currentTemplate.Name;
      }

      this.templateData = params.templateData;

      let templateLanguages = params.templateLanguages;
      if (currentTemplate) {
        //current templates might be assigned to deleted (inactive) languages => keep them
        templateLanguages = currentTemplate.templateLanguages;
        this.languageChoices = this.appSettings.allReportDesignerLocales.filter(l => templateLanguages.indexOf(l.id) !== -1 || l.isActive);
      } else {
        //new templates can be assigned only to active languages
        this.languageChoices = this.appSettings.allReportDesignerLocales.filter(l => l.isActive);
      }

      const isNewTemplate = !this.templateData.oid;//oid not defined or 0
      this.languageIds = isNewTemplate ? [] : templateLanguages;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Save template action handler
   * @returns
   */
  private async handleSaveTemplate() {
    const eventBus = this.eventBus;

    this.templateNameInvalid = (this.templateName || '') === '';
    this.languageInvalid = this.languageIds.length === 0;
    if (this.templateNameInvalid || this.languageInvalid) {
      return;
    }

    if (!this.templateData.pages) {
      eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'emptyReport');
      return;
    }


    this.templateData.name = this.templateName;

    const oid = (!this.saveAs && this.templateData.oid) ? this.templateData.oid : -1;

    const saveData = {
      Oid: oid,
      Name: this.templateName,
      LanguageId: 1,
      Json: this.reportDesignerTemplatePersistanceService.getTemplateStringForSave(this.templateData),
      templateLanguages: this.languageIds
    };

    try {
      this.loading = true;
      const data = await this.saveTemplate(saveData);
      this.loading = false;

      this.templateNameInvalid = false;
      if (!data || data.Oid == 0) {
        eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            //Nothing
          }
        }, 'savingTemplateFailed');
        return;
      }

      this.rdStorage.setTemplate(data);
      this.templateData.oid = data.Oid; // Set the OID here, it might be updated in case of a new template creation

      const currentLanguageId = this.appLocalization.getReportDesignerDictionaryConfig().id;
      let switchToLanguageId = this.languageIds[0];
      if (this.languageIds.indexOf(currentLanguageId) !== -1) {
        switchToLanguageId = currentLanguageId;//current language is still linked to template => stay as is
      }

      const templateConfigurations = this.appSettings.allReportDesignerLocales.filter(l => this.languageIds.indexOf(l.id) !== -1);
      this.appLocalization.setReportDesignerDictionaryConfigurations(templateConfigurations, false);

      this.appLocalization.setReportDesignerDictionaryConfigById(switchToLanguageId).then(() => {
        this.templateData.savedAsNew = this.saveAs;
        eventBus.broadcastEvent("reportDesignerDialog.templateSaved", {
          templateData: this.templateData,
          languageId: currentLanguageId//save in language in which dialog was opened
        });

        //finally, close dialog
        this.cancel();
      });

    } catch (error) {
      this.loading = false;
      if (error?.data?.ExceptionMessage === 'Duplicate template name!') {
        this.templateNameInvalid = true;
        eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            //Nothing
          }
        }, 'reportDesignerDialogDuplicateTemplateName');
      } else {
        alert('Failed to save template!');
      }
    }
  }
}
